import { useEffect, useState } from 'react';
import { useTableId, useTableSelector } from 'hooks/useTableSelector';
import { isSeatDataGap, renderedSeatCount, reportedSeatedCount } from 'utils/tableUsable';

/**
 * How long the seat-data gap must PERSIST before the table is called UNUSABLE.
 *
 * This grace belongs to the consequential verdict only — see the asymmetry note
 * on `useTableUsable`. It is not applied to the loading chip.
 *
 * The cross-check compares two views of occupancy that the server derives
 * separately (`p{i}name` counts vs `p{i}data` seat rows), so a single frame in
 * which they disagree is expected and legitimate — a player joining, standing
 * up, or being moved by the balancer writes those columns at slightly different
 * moments, and the client can render the snapshot in between.
 *
 * 5s is chosen against how often snapshots actually arrive:
 *   - During a live hand the engine delivers 8+ table-state messages within
 *     ~500ms (App.tsx coalesces them into one render per frame), so a genuine
 *     seat change is corrected roughly an order of magnitude inside this window.
 *   - Even a background grid cell, whose updates BackgroundTableBuffer throttles
 *     to at most one every 2s, gets at least two refreshes before the window
 *     closes. A gap that survives all of them is not a render-ordering artifact.
 *   - It stays well under TABLE_LOAD_TIMEOUT_MS (20s) so the state is recognised
 *     early enough for the error modal's own window to run, and under
 *     useFeltHydrationRecovery's 7–10s reconnect delay so the automatic recovery
 *     attempt still lands before the terminal error.
 *
 * The window is a MINIMUM DURATION, not a polling interval: the flag clears on
 * any render where the gap is gone, so a table that repairs itself never has to
 * wait for a fresh snapshot to be declared healthy again.
 */
export const SEAT_DATA_GAP_GRACE_MS = 5000;

/**
 * Anti-flicker settle for the COSMETIC chip only.
 *
 * `seatsPending` is otherwise instantaneous, which lets a sub-frame disagreement
 * between `p{i}name` and `p{i}data` flash the chip on a live felt — and because
 * TableLoadIndicator is `role="status"`, that flash also queues a spurious
 * "Loading table" announcement to a screen reader on a table that is fine.
 *
 * 200ms is chosen NOT as a detection threshold — it is the point below which a
 * loading indicator is bad UI regardless of a11y. A spinner that appears and
 * vanishes inside a fifth of a second reads as a glitch, so suppressing it is an
 * improvement to the visual as well as the announcement.
 *
 * Preferred over dropping `aria-live`: `role="status"` carries an implicit
 * polite live region, so silencing the announcement means removing the role, and
 * that takes the state away from screen-reader users entirely. They should learn
 * the table is loading on the same terms as everyone else; what they should not
 * get is an announcement for a state nobody saw.
 *
 * Deliberately two orders of magnitude below SEAT_DATA_GAP_GRACE_MS — a table
 * that takes a second or two to seat still shows its chip almost immediately,
 * which is the whole point of splitting the thresholds.
 */
export const SEATS_PENDING_SETTLE_MS = 200;

export type TableLoadState = {
  /**
   * COSMETIC, IMMEDIATE (no grace). The server reports players seated and the
   * client has not drawn a single one of them, right now.
   *
   * This is the ordinary "this table is still loading" state a player watches
   * during a normal join: the felt is drawn (table number, blinds, pot, dealer)
   * because those come from the core table columns, while the seats are still
   * blank because they come from `p{i}data` (see utils/tableUsable). It is a
   * label on a spinner — being wrong for a moment costs nothing and self-clears
   * on the next render, so it does NOT wait out the failure grace. Waiting 5s
   * to show a spinner means a table that loads in under 5s never shows one at
   * all, which is exactly the reported complaint.
   *
   * It carries only SEATS_PENDING_SETTLE_MS (200ms) of anti-flicker, which is
   * about not rendering a chip nobody would have seen — not about detection.
   *
   * False positives that WOULD matter are excluded by construction rather than
   * by waiting: an empty table reports `noPlayers: 0`, a payload with no count
   * reports nothing, and a client-side wipe (`players: []`) is not a full-length
   * seat array — so none of them can ever spin. See `hasSeatDataGap`.
   */
  seatsPending: boolean;

  /**
   * CONSEQUENTIAL, GRACED (SEAT_DATA_GAP_GRACE_MS). The table is not usable:
   * either no snapshot has arrived at all (`tabletype` empty — the original
   * EN-10324 case), or the seat-data gap has persisted long enough that it is
   * not a transient skew.
   *
   * This drives things a player pays for when they are wrong: the hydration
   * registry (and through it ConnectionManager's felt-staleness reconnect), the
   * one-shot reconnect in useFeltHydrationRecovery, and the terminal error modal
   * whose only action is to leave the table. Being wrong here can cost someone
   * their seat, so it keeps the grace period AND the 20s timeout behind it.
   */
  unusable: boolean;
};

/**
 * The client's answer to "how loaded is this table?", at TWO thresholds.
 *
 * It replaces the bare `!!game.tabletype` that five call sites used to ask
 * independently (the felt's load indicator, utils/tableHydrationState — and
 * through it ConnectionManager's reconnect gate — useFeltHydrationRecovery, and
 * useTableLoadTimeout behind TableLoadErrorModal). That single signal answered a
 * narrower question, "did ANY snapshot arrive", and a table whose seat data
 * never landed satisfied it — so a felt could be drawn and unusable while every
 * loading and recovery path stood down.
 *
 * THE ASYMMETRY IS THE POINT. One condition, two costs:
 *
 *   showing a spinner early   →  a moment of unnecessary reassurance
 *   declaring a table broken  →  a reconnect, then a modal that ends in the
 *                                player leaving the table
 *
 * A threshold tuned for the second is far too slow for the first. Conflating
 * them is what suppressed the loading animation for the first 5 seconds of
 * every normal join. `seatsPending` fires immediately; `unusable` waits.
 */
export function useTableUsable(): TableLoadState {
  const gameID = useTableId();
  const tableType = useTableSelector((table) => table.game.tabletype);

  // SELECT NARROWLY — scalars only.
  //
  // Subscribing to `table.gameState` or `table.players` wholesale would wake
  // this hook (and with it the whole felt subtree, and every grid cell's error
  // modal) on essentially every server push: `mergeIfChanged` gives those
  // objects a new identity whenever ANY of their fields moves, and during a
  // live hand `isMyTurn`, `playerCallAmount`, `betMultiplier` and friends move
  // constantly. That is exactly the render fan-out reducers/identityMerge.ts
  // and docs/investigations/20260819-game-client-overheating.md exist to stop
  // (50–68ms of blocked main thread per message batch). Deriving the seat count
  // inside the selector collapses the whole players array to a NUMBER, so a
  // re-render only happens when the answer actually changes.
  const reportedSeated = useTableSelector((table) => reportedSeatedCount(table.gameState));
  const drawnSeats = useTableSelector((table) => renderedSeatCount(table.players));
  const seatArrayLength = useTableSelector((table) =>
    Array.isArray(table.players) ? table.players.length : 0
  );

  const gap = isSeatDataGap(seatArrayLength, drawnSeats, reportedSeated);

  // Keyed to the gameID it was confirmed FOR, not a bare boolean.
  //
  // A panel can rebind to a different table without remounting this hook (the
  // carousel's RebindingTableView does exactly that), and an unkeyed flag would
  // carry one table's confirmed failure onto the next — skipping the incoming
  // table's grace entirely. No current render path reaches it (MultiTableGrid
  // keys each cell's subtree by gameID), so this is a latent hole rather than a
  // live bug; both sibling hooks defend the same invariant the same way, and
  // leaving the odd one out is how it stops being latent. See
  // useTableLoadTimeout's `timedOutFor` and useFeltHydrationRecovery's
  // `attemptedRef`.
  const [confirmedFor, setConfirmedFor] = useState<string | null>(null);
  // Same keying for the cosmetic settle, so a rebind cannot inherit the other
  // table's "already settled" state and flash the chip without its own settle.
  const [settledFor, setSettledFor] = useState<string | null>(null);

  useEffect(() => {
    if (!gap || !gameID) {
      setConfirmedFor(null);
      setSettledFor(null);
      return;
    }
    const settle = window.setTimeout(() => setSettledFor(gameID), SEATS_PENDING_SETTLE_MS);
    const confirm = window.setTimeout(() => setConfirmedFor(gameID), SEAT_DATA_GAP_GRACE_MS);
    return () => {
      window.clearTimeout(settle);
      window.clearTimeout(confirm);
    };
  }, [gap, gameID]);

  const gapConfirmed = !!gameID && confirmedFor === gameID;
  const gapSettled = !!gameID && settledFor === gameID;

  return {
    // Gated on `tabletype` only so it describes the SEAT gap specifically; the
    // no-snapshot-at-all case is `unusable`, and the felt shows its chip for the
    // union of the two.
    seatsPending: !!tableType && gap && gapSettled,
    unusable: !tableType || (gap && gapConfirmed)
  };
}
